import React, { Fragment, useEffect, useState } from "react";
import { useQuery, useLazyQuery } from "@apollo/react-hooks";
import { useSelector } from "react-redux";
import { Button, Select, Input, Collapse, DatePicker, Space, Typography, Divider, Skeleton, Tag} from "antd";
import moment from "moment";
import { Link, useHistory } from "react-router-dom";
import {
  PermitStatus,
  UserRole,
  StatusColor,
  PermitStatusArray,
  InterlockDisclaimer,
  UnclosedPermitDisclaimer,
} from "../config";
import { formatDate, getNextReviewer, TABLE_LIST_LIMIT, PERMITTOCLOSED, successNotify, warnNotify } from "../util";
import { GET_LSS_IMPAIRMENT_PERMITS } from "../graphql/modules";

const { Search } = Input;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;


function escapeHtml(str) {
    return str.replace(/[&<>"'`=\/]/g, s => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
        '/': '&#x2F;',
        '`': '&#x60;',
        '=': '&#x3D;'
    }[s]));
}

export const LssImpairmentRequestListing = ({ match }) => {
    const { user: authUser } = useSelector((state) => state.auth);
    const [site, setSite] = useState('F10NX');
    const [searchText, setSearchText] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [rangeValue, setRangeValue] = useState([]);

    const [GetLssImpairmentData, {data: lssImpairmentData, loading: lssImpairmentLoading, error: lssImpairmentError}] = useLazyQuery(GET_LSS_IMPAIRMENT_PERMITS, {
        variables: {
            site: site,
            searchText: searchText,
            startDate: startDate,
            endDate: endDate
        },
        fetchPolicy: "cache-and-network",
    });

    const getDateKeys = () => {
        if (startDate && endDate) {
            const start = moment(startDate);
            const end = moment(endDate);
            const days = end.diff(start, 'days') + 1;
            return Array.from({ length: days }, (_, i) =>
                start.clone().add(i, "days").format("YYYY-MM-DD")
            );
        } else {
            // Default: 15 days (-7 to +7 from today)
            return Array.from({ length: 15 }, (_, i) =>
                moment().add(i - 7, "days").format("YYYY-MM-DD")
            );
        }
    };

    const highlightText = (text, search) => {
        if (!search) return escapeHtml(text || '');
        const escapedText = escapeHtml(text || '');
        const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const highlighted = escapedText.replace(
            regex,
            '<mark style="background: #ffe066; color: #c62828; font-weight: bold;">$1</mark>'
        );
        return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
    };

    const dateKeys = getDateKeys();

    const handleSearchButton = () => {
        console.log("Search button Clicked");
        console.log("Site:", site);
        console.log("Search Text:", searchText);
        console.log("Start Date:", startDate);
        console.log("End Date:", endDate);

        GetLssImpairmentData()
    }

    const handleDateFrom = (dates, dateStrings) => {
        if (dates) {
          const formattedDates = dates.map(date => date.format('YYYY-MM-DD'));
          console.log(formattedDates);
          setStartDate(formattedDates[0]);
          setEndDate(formattedDates[1]);
        } else {
          console.log('No dates selected');
        }
    };

    const disabledDate = (current) => {
        if (!rangeValue || rangeValue.length === 0) {
            return false;
        }
        const tooLate = rangeValue[0] && current.diff(rangeValue[0], 'days') > 30;
        const tooEarly = rangeValue[0] && rangeValue[0].diff(current, 'days') > 0;
        return tooEarly || tooLate;
    };

    const lssPermitsByDateStatus = {};
    dateKeys.forEach(dateKey => {
        lssPermitsByDateStatus[dateKey] = {
            pending: [],
            approved: [],
            executed: [],
            completed: []
        };
    });

    lssImpairmentData?.GetLssImpairmentPermits?.results.forEach(permit => {
        const dateKey = permit.startDate;
        const statusKey = permit.status.toLowerCase();
        if (lssPermitsByDateStatus[dateKey] && lssPermitsByDateStatus[dateKey][statusKey]) {
            lssPermitsByDateStatus[dateKey][statusKey].push(permit);
        }
    });

    // Generate date panels with total permits per date
    const getDatePanels = (lssPermitsByDateStatus) => {
        return dateKeys.map(key => {
            const statusObj = lssPermitsByDateStatus[key] || {};
            const totalNumberOfPermitsPerDate =
                (statusObj.pending?.length || 0) +
                (statusObj.approved?.length || 0) +
                (statusObj.executed?.length || 0) +
                (statusObj.completed?.length || 0);

            return {
                key,
                label: (
                    <>
                        LSS/FAS Impairment Start Date and Time: <strong>{moment(key).format("DD-MM-YYYY")}</strong> ({totalNumberOfPermitsPerDate})
                    </>
                )
            };
        });
    };

    const datePanels = getDatePanels(lssPermitsByDateStatus).sort((a, b) => moment(b.key).diff(moment(a.key)));;

    const renderPermitStatusCell = (row) => {
        const status = row.status
        let color = "orange";
        if (status === PermitStatus.Rejected) color = StatusColor.Rejected;
        else if (status === PermitStatus.Approved) color = StatusColor.Approved;
        else if (status === PermitStatus.Completed) color = StatusColor.Completed;
        else if (status === PermitStatus.Cancelled) color = StatusColor.Cancelled;
        else if (status === PermitStatus.PendingClosure) color = StatusColor.PendingClosure;
        else if (status === PermitStatus.PendingCancel) color = StatusColor.PendingCancel;

        const approvedBy = row?.approvedBy?.length ? row.approvedBy[row?.approvedBy?.length - 1] : null;
        const rejectedBy = row?.rejectedBy;

        let nextReviewer = getNextReviewer(row);

        return (
          <div className="LssImpairmentRequestListingTableCell">
            {status === PermitStatus.Pending ? (
              <Tag
                style={{
                  borderRadius: 10,
                  padding: "0px 10px",
                  width: "100%",
                  justifyContent: "space-around",
                  display: "flex",
                }}
                color={color}
              >
                Pending Approval By PM
              </Tag>
            ) : status === PermitStatus.PendingApproval ? (
              <Tag
                style={{
                  borderRadius: 10,
                  padding: "0px 10px",
                  width: "100%",
                  justifyContent: "space-around",
                  display: "flex",
                }}
                color={color}
              >
                Pending Approval By {nextReviewer}
              </Tag>
            ) : status === PermitStatus.Rejected ? (
              <Tag
                style={{
                  borderRadius: 10,
                  padding: "0px 10px",
                  width: "100%",
                  justifyContent: "space-around",
                  display: "flex",
                }}
                color={color}
              >
                Rejected By {rejectedBy?.group?.name ? rejectedBy?.group?.name : rejectedBy?.role}
              </Tag>
            ) : status === PermitStatus.Approved && approvedBy ? (
              <Tag
                style={{
                  borderRadius: 10,
                  padding: "0px 10px",
                  width: "100%",
                  justifyContent: "space-around",
                  display: "flex",
                }}
                color={color}
              >
                Approved By {approvedBy?.group?.name ? approvedBy?.group?.name : approvedBy.role}
              </Tag>
            ) : status === undefined || status === null ? (
              " "
            ) : (
              <Tag
                style={{
                  borderRadius: 10,
                  padding: "0px 10px",
                  width: "100%",
                  justifyContent: "space-around",
                  display: "flex",
                }}
                color={color}
              >
                {status}
              </Tag>
            )}
          </div>
        );
      }

    // Helper to render the table
    const renderPermitTable = (permits) => (
        <div style={{ overflowX: 'auto' }}>
            <table className="table table-bordered table-sm mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                <thead>
                    <tr>
                        <th style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>Parent Permit Number</th>
                        <th style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>Parent Permit Approval Status</th>
                        <th style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>LSS Permit Number</th>
                        <th style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>LSS Child Permit Approval Status</th>
                        <th style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>LSS Permit Requester Name</th>
                        <th style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>LSS Permit host name</th>
                        <th style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>Reason for Impairment</th>
                        <th style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>Type of LSS need to impair</th>
                        <th style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>Start Date and Time</th>
                        <th style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>End Date and Time</th>
                        <th style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>Site</th>
                        <th style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>Affected Area</th>
                    </tr>
                </thead>
                <tbody>
                    {permits.length === 0 ? (
                        <tr>
                            <td colSpan={5} className="text-center">No permits</td>
                        </tr>
                    ) : (
                        permits.map((permit, idx) => (
                            <tr key={idx}>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                    <Link
                                        to={{
                                            pathname: `/permits/${permit.parentPermitId}`,
                                        }}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {permit.parentPermitNumber}
                                    </Link>
                                </td>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                    {renderPermitStatusCell(permit.parentPermit)}
                                </td>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                    <Link
                                        to={{
                                            pathname: `/childpermits/${permit.childPermitId}`,
                                        }}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {permit.lssChildPermitNumber}
                                    </Link>
                                </td>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                    {renderPermitStatusCell(permit.lssChildPermit)}
                                </td>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{permit.requesterName}</td>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{permit.hostName}</td>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                    {highlightText(permit.workDescription || '', searchText)}
                                </td>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{permit.typeOfLssNeedToImpair}</td>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{permit.startDateAndTime}</td>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{permit.endDateAndTime}</td>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{permit.site}</td>
                                <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{permit.affectedArea}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );

    return (
        <Fragment>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <h1>F10 LSS/FAS Impairment Request Listing</h1>
                    </div>
                </div>

                <div className="container-fluid">
                    <div className="row" hidden={authUser?.role === UserRole.SuperAdmin || authUser?.role === UserRole.ContractorPM}>
                        <Link
                            to={{
                                pathname: `childpermits/create-child-permit`,
                                // Pass the LSS template ID
                                state: { lssTemplateId: '604b0ace05a8ed2d4d4bbe60' } // Replace with actual ID or logic
                            }}
                            className="btn btn-primary mr-2"
                            
                            disabled={authUser?.role === UserRole.SuperAdmin || authUser?.role === UserRole.ContractorPM}
                        >
                            <i className="fas fa-plus-circle" style={{ marginRight: 8 }}></i>
                            New Impairment Request
                        </Link>
                    </div>
                    <Divider style={{ margin: '16px 0' }} />
                    <div className="row">
                        <div className="col-2 d-flex align-items-center justify-content-center">
                            <Select
                                style={{ width: '100%' }}
                                placeholder="Select LSS View"
                                value={site}
                                options={[
                                    { value: 'F10NX', label: 'F10N/X LSS View' },
                                    { value: 'F10A', label: 'F10A LSS View' },
                                    { value: 'F10W', label: 'F10W LSS View' },
                                    { value: 'MSH', label: 'MSH LSS View' },
                                ]}
                                onChange={value => setSite(value)}
                            />
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-center">
                            <Search
                                placeholder="Find an item"
                                style={{ width: '100%' }}
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                            />
                        </div>
                        <div className="col-4 d-flex align-items-center justify-content-left">
                            <RangePicker 
                                className="me-2" style={{ minWidth: 300, marginRight: 8 }} 
                                id={{
                                    start: 'startInput',
                                    end: 'endInput',
                                }}
                                value={rangeValue}
                                onCalendarChange={val => setRangeValue(val)}
                                disabledDate={disabledDate}
                                onChange={handleDateFrom}
                            />
                            <Button style={{ minWidth: 200 , marginRight: 8}}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleSearchButton();
                                }}
                                disabled={lssImpairmentLoading}
                            >
                                <i className="fas fa-search" style={{ marginRight: 8 }}></i>
                                Search
                            </Button>
                            <Button
                                style={{ minWidth: 200, marginRight: 8 }}
                                disabled={lssImpairmentLoading}
                                onClick={() => {
                                    setSite('F10NX');
                                    setSearchText('');
                                    setStartDate('');
                                    setEndDate('');
                                    setRangeValue([]);
                                    // Optionally, you can also refetch or reset data here if needed
                                }}
                            >
                                <i className="fas fa-eraser" style={{ marginRight: 8 }}></i>
                                Clear
                            </Button>
                            
                        </div>

                    </div>
                </div>
                <div className="container-fluid">
                    <div className="row">
                        <div className="container-fluid mt-4">
                            {lssImpairmentLoading ? (
                                <Skeleton
                                    active
                                    paragraph={{ rows: 8 }}
                                    title={{ width: '60%' }}
                                />
                            ) : (
                                <Collapse accordion>
                                    {datePanels.map(panel => (
                                        <Panel header={panel.label} key={panel.key}>
                                            <Collapse>
                                                {["pending", "approved", "executed", "completed"].map(statusKey => (
                                                    <Panel
                                                        header={
                                                            <>
                                                                Status by Operations / EHS Manager -{" "}
                                                                <strong style={{ color: 'red' }}>
                                                                    {statusKey.toUpperCase()}
                                                                </strong>
                                                                {" "}({(lssPermitsByDateStatus[panel.key]?.[statusKey]?.length || 0)})
                                                            </>
                                                        }
                                                        key={statusKey}
                                                    >
                                                        {renderPermitTable(
                                                            lssPermitsByDateStatus[panel.key]
                                                                ? lssPermitsByDateStatus[panel.key][statusKey]
                                                                : []
                                                        )}
                                                    </Panel>
                                                ))}
                                            </Collapse>
                                        </Panel>
                                    ))}
                                </Collapse>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};